import * as React from 'react'
import { useState } from 'react'

import { Box, Button, Text } from '@factorialco/deprecated-design-system'
import { AvailableModules } from 'generated/featureDefinitions'

import { observer } from 'mobx-react'

import Modal from 'modules/core/components/Modal'
import { useEffect } from 'modules/core/hooks/useEffect'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'

import type { CreateProps as UpsellRequestProps } from 'modules/upsell/hooks/useCreateUpsellRequest'
import useCreateUpsellRequest from 'modules/upsell/hooks/useCreateUpsellRequest'

export type UpgradePlanModalOrigin = 'createJobOpening'

interface UpgradePlanModalProps {
  onClose: () => void
  origin: UpgradePlanModalOrigin
  upsellingProps: Omit<UpsellRequestProps, 'moduleRequested'>
}

const trackGoToUpgrade = () => {
  track(trackingEvents.ats.goToUpgradeNow, { origin: 'ats_upgrade_modal' })
}

export const UpgradePlanModal = observer(
  ({ onClose, origin, upsellingProps }: UpgradePlanModalProps) => {
    const [success, setSubmitSucces] = useState(false)
    const { create, loading: submitting } = useCreateUpsellRequest()
    const onSubmit = async () => {
      setSubmitSucces(false)
      const result = await create({ moduleRequested: AvailableModules.ats, ...upsellingProps })

      setSubmitSucces(handleResult(result, () => undefined).success)
    }

    return success ? (
      <ThankYouModal onClose={onClose} origin={origin} />
    ) : (
      <UpgradeModal onClose={onClose} onSubmit={onSubmit} submitting={submitting} origin={origin} />
    )
  }
)

interface UpgradeModalProps {
  onClose: () => void
  onSubmit: () => void
  submitting: boolean
  origin: UpgradePlanModalOrigin
}

const UpgradeModal = ({ onClose, onSubmit, submitting }: UpgradeModalProps) => {
  useEffect(() => {
    track(trackingEvents.ats.upgradeModalView, { reason: 'job_opening' })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  return (
    <Modal
      title={i18n.t('ats.upgrade_modal.title')}
      illustration="fireworks"
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose} type="secondary">
            {i18n.t('ats.upgrade_modal.actions.cancel')}
          </Button>
          <Button onClick={onSubmit} disabled={submitting} iconName="star">
            {i18n.t('ats.upgrade_modal.actions.confirm')}
          </Button>
        </>
      }
    >
      <Box>
        <Text.Normal weight="medium" color="grey800">
          {i18n.t('ats.upgrade_modal.description')}
        </Text.Normal>
      </Box>
    </Modal>
  )
}

interface ThankYouModalProps {
  onClose: () => void
  origin: UpgradePlanModalOrigin
}

const ThankYouModal = ({ onClose }: ThankYouModalProps) => {
  useEffect(() => trackGoToUpgrade(), [])

  return (
    <Modal
      illustration="contact-us"
      title={i18n.t('ats.upgrade_modal.thank_you_modal.title')}
      description={i18n.t('ats.upgrade_modal.thank_you_modal.description')}
      onClose={onClose}
      footer={
        <Button type="primary" onClick={onClose}>
          {i18n.t('ats.upgrade_modal.thank_you_modal.close')}
        </Button>
      }
    />
  )
}
